import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './SimulateExam.css';
import './PracticeTest.css';
import './PracticeTest.responsive.css';
import './PracticeTest.small-screen.css';
import examTypeService from '../services/examTypeService';
import examService from '../services/examService';
import { getLinkHref } from '../services/hateoasUtils';
import { useTheme } from '../contexts/ThemeContext';
import { ensureMinimalAnswersLinkOnQuestion } from '../utils/questionUtils';
import { QuotaExceededError, isQuotaError, getQuotaMessage, normalizeQuotaPayload } from '../lib/quota.js';

// Import practice test components for answer handling
import ExamAnswerOptions from './SimulateExam/ExamAnswerOptions';
import ExamSetupContainer from './SimulateExam/ExamSetupContainer';
import ExamSetupForm from './SimulateExam/ExamSetupForm';
import LabTable from './practiceTest/LabTable';
import AssetDisplay from './practiceTest/AssetDisplay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faSpinner } from '@fortawesome/free-solid-svg-icons';

// Import timer service for session-level time tracking
import examTimerService from '../services/examTimerService';
// Import exam API for HATEOAS-based exam ending
import examApi from '../services/api/examApi';

const SimulateExam = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  // Debug theme
  useEffect(() => {
    // console.log('Theme state in SimulateExam:', { isDarkMode });
    // console.log('Body data-theme:', document.body.getAttribute('data-theme'));
  }, [isDarkMode]);

  // Helper function to detect and process quota errors from any API response
  const handleQuotaError = useCallback((err) => {
    // console.log('🔍 handleQuotaError called with:', err);
    // console.log('🔍 Error message:', err.message);
    // console.log('🔍 Error status:', err.status);
    // console.log('🔍 Error data:', err.data);
    // console.log('🔍 Error data status:', err.data?.status);
    
    // Check if this is a quota error based on status and data
    // The HTTP response might be 500, but the actual quota status is in the data
    const isQuotaLikeError = err.data && (
      err.data.status === 403 || // Check data status first
      err.status === 403 ||       // Then check HTTP status
      err.data.title === 'Plan Limit Exceeded' ||
      err.data.detail ||
      err.data.usageMetrics ||
      err.data.UsageMetrics || // Note: API uses capital U
      err.data.limit !== undefined ||
      err.data.usage !== undefined
    );
    
    // console.log('🔍 isQuotaLikeError:', isQuotaLikeError);
    // console.log('🔍 isQuotaError(err):', isQuotaError(err));
    
    if (isQuotaError(err) || isQuotaLikeError) {
      let quotaMessage;
      if (isQuotaError(err)) {
        // It's already a QuotaExceededError
        // console.log('🔍 Using QuotaExceededError quota data');
        quotaMessage = getQuotaMessage(err.quota);
      } else {
        // It's a regular error with quota data, convert it
        // console.log('🔍 Converting regular error to quota format');
        // Normalize the data structure - API uses UsageMetrics (capital U)
        const normalizedData = {
          ...err.data,
          usageMetrics: err.data.usageMetrics || err.data.UsageMetrics
        };
        const normalizedQuota = normalizeQuotaPayload(normalizedData);
        // console.log('🔍 Normalized quota:', normalizedQuota);
        quotaMessage = getQuotaMessage(normalizedQuota);
      }
      // console.log('🔍 Generated quota message:', quotaMessage);
      return quotaMessage;
    }
    
    // console.log('🔍 Not a quota error, returning null');
    return null;
  }, []);

  const [currentView, setCurrentView] = useState('setup'); // 'setup', 'exam', 'break', 'completed'
  const [examTypes, setExamTypes] = useState([]);
  const [selectedExamType, setSelectedExamType] = useState('');
  const [examSession, setExamSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isQuotaErrorState, setIsQuotaErrorState] = useState(false);
  
  // Exam state
  const [currentBlock, setCurrentBlock] = useState(1);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [questions, setQuestions] = useState([]);  const [answers, setAnswers] = useState({});
  const [examStartTime, setExamStartTime] = useState(null);  // Answer handling state (following practice test patterns)
  const [selectedAnswer, setSelectedAnswerState] = useState(null);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
  const [questionFlags, setQuestionFlags] = useState({}); // Track flagged questions
  const [showEndBlockModal, setShowEndBlockModal] = useState(false); // Modal state for end block confirmation
  const [showEndSessionModal, setShowEndSessionModal] = useState(false); // Modal state for end session confirmation
  
  // Question Assets State
  const [currentQuestionAssets, setCurrentQuestionAssets] = useState([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [assetsError, setAssetsError] = useState(null);
  // Wrapper for setSelectedAnswer with debugging
  const setSelectedAnswer = useCallback((newAnswer) => {
    // console.log('SimulateExam: *** setSelectedAnswer called ***:', {
    //   to: newAnswer,
    //   timestamp: new Date().toISOString()
    // });
    setSelectedAnswerState(newAnswer);
  }, []); // Empty deps to make it stable - using setSelectedAnswerState directly
  // Refs for answer submission coordination
  const examAnswerOptionsRef = useRef(null);
  const [submitTrigger, setSubmitTrigger] = useState(0);
  // Timer state - Enhanced for block-based timing
  const [timeRemaining, setTimeRemaining] = useState(0); // Total exam time
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0); // Current block time
  const [totalExamTimeSpent, setTotalExamTimeSpent] = useState(0); // Total time spent across all blocks
  const [breakTimeRemaining, setBreakTimeRemaining] = useState(0);
  const [sessionState, setSessionState] = useState('not_started'); // 'not_started', 'block_running', 'break_running', 'paused', 'finished'
  const [completedBlocks, setCompletedBlocks] = useState(new Set()); // Track completed blocks
  const [activeBlock, setActiveBlock] = useState(1); // Track which block is currently active/accessible
  const masterTimerRef = useRef(null);
  
  // Exam timer service state (for session-level time tracking like practice tests)
  const [examTimerRunning, setExamTimerRunning] = useState(false);

  // Timer update callback function for HATEOAS API updates (same as practice test)
  const handleExamTimerUpdate = useCallback(async (timeSpent) => {
    if (!examSession) {
      // console.log('SimulateExam: No exam session available for timer update');
      return;
    }

    try {
      const updatePayload = {
        timeSpent: timeSpent  // Backend expects timeSpent in seconds
      };

      // console.log('SimulateExam: Sending exam timer update:', {
      //   examId: examSession.id,
      //   timeSpent,
      //   payload: updatePayload
      // });

      // Use examService.updateExamSession method to send the update
      await examService.updateExamSession(examSession.id, updatePayload);
      // console.log('SimulateExam: Exam timer update successful');
    } catch (error) {
      console.error('SimulateExam: Error updating exam timer:', error);
    }
  }, [examSession]);

  // Exam formats configuration - Enhanced with block timing
  // Using both name-based and ID-based mapping for flexibility
  const examFormats = {
    // Name-based mapping (fallback for backward compatibility)
    'CBSE': {
      totalQuestions: 200,
      blocks: 4,
      questionsPerBlock: 50,
      blockTimeMinutes: 75, // 1 hour 15 minutes per block
      totalTime: 5 * 60 + 15, // 5 hours 15 minutes total
      breakTime: 45 // 45 minutes total break time
    },
    'STEP 1': {
      totalQuestions: 280,
      blocks: 7,
      questionsPerBlock: 40,
      blockTimeMinutes: 60, // 1 hour per block
      totalTime: 8 * 60, // 8 hours total
      breakTime: 45 // 45 minutes total break time
    },
    'STEP 2': {
      totalQuestions: 320,
      blocks: 8,
      questionsPerBlock: 40,
      blockTimeMinutes: 60, // 1 hour per block
      totalTime: 9 * 60, // 9 hours total
      breakTime: 45 // 45 minutes total break time
    },
    // Alternative name mappings
    'USMLE Step 1': {
      totalQuestions: 280,
      blocks: 7,
      questionsPerBlock: 40,
      blockTimeMinutes: 60, // 1 hour per block
      totalTime: 8 * 60, // 8 hours total
      breakTime: 45 // 45 minutes total break time
    },
    'USMLE Step 2': {
      totalQuestions: 320,
      blocks: 8,
      questionsPerBlock: 40,
      blockTimeMinutes: 60, // 1 hour per block
      totalTime: 9 * 60, // 9 hours total
      breakTime: 45 // 45 minutes total break time
    }
  };

  // ID-based mapping for more reliable format lookup
  const getExamFormatByType = (examType) => {
    if (!examType) {
      // console.log('getExamFormatByType - no examType provided, returning CBSE default');
      return examFormats['CBSE'];
    }

    // console.log('getExamFormatByType - examType:', examType);
    
    // Try exact name match first
    if (examFormats[examType.name]) {
      // console.log('getExamFormatByType - found by name:', examType.name);
      return examFormats[examType.name];
    }

    // Try ID-based mapping for known exam types
    // Assuming common ID patterns based on typical database setups
    switch (examType.id) {
      case 1:
        // console.log('getExamFormatByType - using ID mapping for CBSE (ID: 1)');
        return examFormats['CBSE'];
      case 2:
        // console.log('getExamFormatByType - using ID mapping for STEP 1 (ID: 2)');
        return examFormats['STEP 1'];
      case 3:
        // console.log('getExamFormatByType - using ID mapping for STEP 2 (ID: 3)');
        return examFormats['STEP 2'];
      default:
        // Fallback: try name-based mapping with common variations
        const nameVariations = [
          examType.name,
          examType.name?.replace('USMLE ', ''),
          examType.name?.replace('Step ', 'STEP '),
          examType.name?.replace('STEP ', 'Step ')
        ].filter(Boolean);
        
        for (const variation of nameVariations) {
          if (examFormats[variation]) {
            // console.log('getExamFormatByType - found by name variation:', variation);
            return examFormats[variation];
          }
        }
        
        // console.log('getExamFormatByType - no match found, returning CBSE default. Available formats:', Object.keys(examFormats));
        return examFormats['CBSE'];
    }
  };
  
  useEffect(() => {
    loadExamTypes();
    return () => {
      // Cleanup master timer
      if (masterTimerRef.current) clearInterval(masterTimerRef.current);
    };
  }, []);

  // Master timer effect - Single source of truth for all timing
  useEffect(() => {
    // Clear any existing timer
    if (masterTimerRef.current) {
      clearInterval(masterTimerRef.current);
      masterTimerRef.current = null;
    }

    // Only start timer if we have an active session state
    if (sessionState === 'block_running' || sessionState === 'break_running') {
      masterTimerRef.current = setInterval(() => {
        switch (sessionState) {
          case 'block_running':
            // Decrement both total time and block time
            setTimeRemaining(prev => {
              if (prev <= 1) {
                // Total exam time is up
                setSessionState('finished');
                completeExam();
                return 0;
              }
              return prev - 1;
            });
            
            setBlockTimeRemaining(prev => {
              if (prev <= 1) {
                // Block time is up - auto end block
                handleBlockTimeExpired();
                return 0;
              }
              return prev - 1;
            });
            
            // Track total time spent
            setTotalExamTimeSpent(prev => prev + 1);
            break;
              case 'break_running':
            // Decrement both break time AND total exam time (break counts against total time)
            setTimeRemaining(prev => {
              if (prev <= 1) {
                // Total exam time is up during break
                setSessionState('finished');
                completeExam();
                return 0;
              }
              return prev - 1;
            });            setBreakTimeRemaining(prev => {
              if (prev <= 1) {
                // Break time is up, automatically resume exam
                resumeFromBreak();
                return 0;
              }
              return prev - 1;
            });
            
            // Track total time spent (including break time)
            setTotalExamTimeSpent(prev => prev + 1);
            break;
            
          default:
            // 'paused', 'not_started', 'finished' - do nothing
            break;
        }
      }, 1000);
    }

    // Cleanup on state change
    return () => {
      if (masterTimerRef.current) {
        clearInterval(masterTimerRef.current);
        masterTimerRef.current = null;
      }
    };
  }, [sessionState]); // Only depend on sessionState

  // Exam timer service effect - start/stop session-level time tracking
  useEffect(() => {
    // Start timer when exam session begins and exam is in progress
    if (examSession && currentView === 'exam' && (sessionState === 'block_running' || sessionState === 'break_running') && !examTimerRunning) {
      // console.log('SimulateExam: Starting exam timer service for exam:', {
      //   examId: examSession.id,
      //   currentView,
      //   sessionState,
      //   existingTimeSpent: examSession.timeSpent || 0
      // });
      
      // Calculate initial time spent if this is a resumed session
      const existingTimeSpent = examSession.timeSpent || 0;
      examTimerService.start(existingTimeSpent, handleExamTimerUpdate);
      setExamTimerRunning(true);
    }
    
    // Stop timer when exam is finished or completed
    if (examSession && (currentView === 'completed' || sessionState === 'finished') && examTimerRunning) {
      // console.log('SimulateExam: Stopping exam timer service - exam completed');
      examTimerService.stop();
      setExamTimerRunning(false);
    }

    // Cleanup timer when component unmounts
    return () => {
      if (examTimerRunning) {
        // console.log('SimulateExam: Stopping exam timer service on cleanup');
        examTimerService.stop();
        setExamTimerRunning(false);
      }
    };
  }, [examSession, currentView, sessionState, examTimerRunning, handleExamTimerUpdate]);

  // Debug effect to track selectedExamType changes and format updates
  useEffect(() => {
    // console.log('SimulateExam: selectedExamType changed:', selectedExamType);
    if (selectedExamType && examTypes.length > 0) {
      const format = getExamFormat();
      // console.log('SimulateExam: Current exam format for selected type:', format);
    }
  }, [selectedExamType, examTypes]); // Depend on both to ensure updates

  // Debug effect to track currentView changes
  useEffect(() => {
    // console.log('SimulateExam: currentView changed to:', currentView);
    // console.log('SimulateExam: Component state at view change:', {
    //   currentView,
    //   questionsLength: questions.length,
    //   examSession: !!examSession,
    //   selectedExamType,
    //   currentBlock,
    //   currentQuestion
    // });  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView]);  // Effect to handle question state when navigating between questions
  useEffect(() => {
    if (currentView === 'exam' && questions.length > 0) {
      resetQuestionState();
      
      // Load assets for the current question
      const currentQuestionData = questions[currentQuestion - 1];
      if (currentQuestionData?.id) {
        loadQuestionAssets(currentQuestionData.id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, currentView]); // Intentionally excluding questions.length, resetQuestionState, and loadQuestionAssets to prevent loops

  // Track selectedExamType changes to ensure format updates
  useEffect(() => {
    if (selectedExamType && examTypes.length > 0) {
      // console.log('selectedExamType changed:', selectedExamType);
      
      const examType = examTypes.find(type => 
        type.id === selectedExamType || 
        type.id === parseInt(selectedExamType) || 
        type.id.toString() === selectedExamType
      );
      
      if (examType) {
        // console.log('Found matching exam type:', examType);
        const format = getExamFormatByType(examType);
        // console.log('Updated format for exam type:', format);
      } else {
        // console.log('No matching exam type found for selectedExamType:', selectedExamType);
      }
    }
  }, [selectedExamType, examTypes]);

  const loadExamTypes = async () => {
    try {
      setLoading(true);
      const types = await examTypeService.getExamTypes();
      // console.log('SimulateExam: Loaded exam types:', types);
      setExamTypes(types || []);
    } catch (err) {
      // Check if this is a quota exceeded error and display detailed message
      const quotaMessage = handleQuotaError(err);
      if (quotaMessage) {
        setError(quotaMessage);
        setIsQuotaErrorState(true);
        console.error('Quota exceeded loading exam types:', err);
      } else {
        setError('Failed to load exam types');
        setIsQuotaErrorState(false);
        console.error('Error loading exam types:', err);
      }
    } finally {
      setLoading(false);
    }
  };  const startExam = async () => {
    // console.log('SimulateExam: startExam called');
    // console.log('SimulateExam: selectedExamType value:', selectedExamType);
    // console.log('SimulateExam: Available exam types:', examTypes);
    // console.log('SimulateExam: Current loading state:', loading);
    
    // Prevent double-clicking
    if (loading) {
      // console.log('SimulateExam: Already loading, ignoring duplicate call');
      return;
    }
    
    if (!selectedExamType) {
      setError('Please select an exam type');
      // console.log('SimulateExam: No exam type selected');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setIsQuotaErrorState(false);
      // console.log('SimulateExam: Starting exam setup process');      // Find the selected exam type
      // console.log('SimulateExam: Looking for examType with ID:', selectedExamType, 'type:', typeof selectedExamType);
      examTypes.forEach((type, index) => {
        // console.log(`SimulateExam: examTypes[${index}]:`, type, 'ID type:', typeof type.id);
      });
      
      const examType = examTypes.find(type => 
        type.id === selectedExamType || 
        type.id === parseInt(selectedExamType) || 
        type.id.toString() === selectedExamType
      );
      // console.log('SimulateExam: Selected exam type:', examType);
      
      if (!examType) {
        throw new Error('Selected exam type not found');
      }      // Get exam format
      const format = getExamFormatByType(examType);
      // console.log('SimulateExam: Using exam format:', format);
      
      // Calculate end date (current time + total exam time)
      const endDate = new Date();
      endDate.setMinutes(endDate.getMinutes() + format.totalTime);
      // console.log('SimulateExam: End date calculated:', endDate.toISOString());

      // Prepare start exam request
      const startExamRequest = {
        examTypeId: selectedExamType,
        type: "Simulated",
        title: `Simulated ${examType.name} Exam`,
        endDate: endDate.toISOString(),
        limit: format.totalQuestions
      };
      // console.log('SimulateExam: Start exam request:', startExamRequest);      // Start exam session
      // console.log('SimulateExam: Calling examService.startExamSession');
      const session = await examService.startExamSession(startExamRequest);
      // console.log('SimulateExam: Exam session created:', session);
      
      // Load first block of questions using the session data directly      console.log('SimulateExam: Loading questions for block 1');
      await loadQuestionsFromSession(session, 1);
      
      // Set state after successful question loading
      // console.log('SimulateExam: Setting state after successful question loading...');
      try {
        setExamSession(session);
        // console.log('SimulateExam: ✓ setExamSession completed');
          setExamStartTime(new Date());
        // console.log('SimulateExam: ✓ setExamStartTime completed');
        
        setTimeRemaining(format.totalTime * 60); // Convert to seconds
        // console.log('SimulateExam: ✓ setTimeRemaining completed');
        
        setBlockTimeRemaining(format.blockTimeMinutes * 60); // Set initial block time
        // console.log('SimulateExam: ✓ setBlockTimeRemaining completed');
          setActiveBlock(1); // Start with block 1 active
        // console.log('SimulateExam: ✓ setActiveBlock completed');

        // console.log('SimulateExam: BEFORE setCurrentView - currentView:', currentView);
        setCurrentView('exam');
        // console.log('SimulateExam: ✓ setCurrentView(exam) completed');
        
        // Add a timeout to check if state actually updated
        setTimeout(() => {
          // console.log('SimulateExam: currentView after timeout:', currentView);
        }, 100);
        
        setCurrentBlock(1);
        // console.log('SimulateExam: ✓ setCurrentBlock completed');
          setCurrentQuestion(1);
        // console.log('SimulateExam: ✓ setCurrentQuestion completed');
        
        // Start timer by setting session state
        // console.log('SimulateExam: Starting timer');
        setSessionState('block_running');
        // console.log('SimulateExam: ✓ setSessionState completed');
          // console.log('SimulateExam: All state updates completed successfully');
      } catch (stateError) {
        console.error('SimulateExam: Error during state updates:', stateError);
        throw stateError;
      }
      
    } catch (err) {
      // Check if this is a quota exceeded error and display detailed message
      // console.log('SimulateExam: Error caught in startExam:', err);
      // console.log('SimulateExam: Error status:', err.status);
      // console.log('SimulateExam: Error data:', err.data);
      // console.log('SimulateExam: Error message:', err.message);
      
      const quotaMessage = handleQuotaError(err);
      // console.log('🚨 SimulateExam startExam: Quota message from helper:', quotaMessage);
      
      if (quotaMessage) {
        // console.log('🚨 SimulateExam startExam: Setting quota error message:', quotaMessage);
        setError(quotaMessage);
        setIsQuotaErrorState(true);
        console.error('Quota exceeded starting exam:', err);
      } else {
        // console.log('🚨 SimulateExam startExam: Processing as regular error, message:', err.message);
        setError(err.message || 'Failed to start exam');
        setIsQuotaErrorState(false);
        console.error('Error starting exam:', err);
      }
    } finally {
      setLoading(false);    }
  };
  const loadQuestionsFromSession = async (session, blockNumber) => {
    try {
      // console.log('SimulateExam: loadQuestionsFromSession called for block:', blockNumber);
      // console.log('SimulateExam: session:', session);
      
      if (!session) {
        console.error('SimulateExam: No session provided');
        throw new Error('No exam session provided');
      }

      let questions = [];
      
      // Try different approaches to get questions based on practice page implementation
      if (session._links && session._links.questions && session._links.questions.href) {
        // console.log('SimulateExam: Using questions link:', session._links.questions.href);
        questions = await examService.getExamQuestionsByLink(session._links.questions.href);
      } else if (session._links && session._links.getQuestions) {
        // console.log('SimulateExam: Using getQuestions link:', session._links.getQuestions.href);
        questions = await examService.getExamQuestionsByLink(session._links.getQuestions.href);
      } else if (session.examQuestions && Array.isArray(session.examQuestions)) {
        // console.log('SimulateExam: Using examQuestions from session');
        questions = session.examQuestions;
      } else if (session.questions && Array.isArray(session.questions)) {
        // console.log('SimulateExam: Using questions from session');
        questions = session.questions;
      } else if (session[session.id] && session[session.id].examQuestions) {
        // Handle format where session ID is a property name containing exam data
        // console.log('SimulateExam: Using session[session.id].examQuestions format');
        questions = session[session.id].examQuestions;
      } else {
        // Try to fetch questions using the exam ID as final fallback
        // console.log('SimulateExam: Attempting to fetch questions using exam ID:', session.id);
        const questionsLink = `/api/exam/${session.id}/questions`;
        questions = await examService.getExamQuestionsByLink(questionsLink);
      }

      // console.log('SimulateExam: Questions response:', questions);
      // console.log('SimulateExam: Questions type:', typeof questions);
      // console.log('SimulateExam: Questions length:', Array.isArray(questions) ? questions.length : 'not array');
      
      if (questions && Array.isArray(questions) && questions.length > 0) {
        // Store all questions for the exam
        const totalQuestions = questions;
        // console.log('SimulateExam: Total questions received:', totalQuestions.length);
        
        // Process questions to ensure they have proper HATEOAS links and examId
        const processedQuestions = totalQuestions.map(question => {
          const processedQuestion = ensureMinimalAnswersLinkOnQuestion(question, session.id);
          
          // Add a response object similar to how PracticeTest does it
          if (!processedQuestion.response) {
            processedQuestion.response = {
              questionId: processedQuestion.id,
              examId: session.id,
              timeSpent: 0,
              isFlagged: false,
              isCorrect: null,
              answerIds: [],
              _links: {
                save: {
                  href: `/api/exam/${session.id}/question/${processedQuestion.id}`,
                  method: 'POST',
                  rel: 'save'
                }
              }
            };
          }
          
          return processedQuestion;
        });
        
        // console.log('SimulateExam: Questions processed with HATEOAS links and response objects');
        
        // For simulated exams, we need to slice the questions for the current block
        const questionsPerBlock = getQuestionsPerBlock();
        const startIndex = (blockNumber - 1) * questionsPerBlock;
        const endIndex = startIndex + questionsPerBlock;
        const blockQuestions = processedQuestions.slice(startIndex, endIndex);
        
        // console.log('SimulateExam: Block questions slice:', {
        //   blockNumber,
        //   questionsPerBlock,
        //   startIndex,
        //   endIndex,
        //   blockQuestionsLength: blockQuestions.length,
        //   totalLength: processedQuestions.length
        // });
        
        setQuestions(blockQuestions);
        
        // Store all processed questions for future block navigation
        if (!window.examAllQuestions) {
          window.examAllQuestions = processedQuestions;
        }
        
        // console.log('SimulateExam: Questions loaded for block', blockNumber, ':', blockQuestions.length);
      } else {
        throw new Error('No questions received or invalid format');
      }
    } catch (err) {
      // Check if this is a quota exceeded error and display detailed message
      const quotaMessage = handleQuotaError(err);
      if (quotaMessage) {
        setError(quotaMessage);
        console.error('Quota exceeded loading questions:', err);
      } else {
        setError('Failed to load questions');
        console.error('SimulateExam: Error loading questions:', err);
      }
      throw err; // Re-throw to prevent exam from starting
    }
  };  const loadQuestions = async (blockNumber) => {
    try {
      setLoading(true);
      // console.log(`SimulateExam: Loading questions for block ${blockNumber}`);
      
      const startIndex = (blockNumber - 1) * getQuestionsPerBlock();
      const endIndex = startIndex + getQuestionsPerBlock();
      
      if (window.examAllQuestions && window.examAllQuestions.length > 0) {
        // Questions are already processed with HATEOAS links and response objects
        const blockQuestions = window.examAllQuestions.slice(startIndex, endIndex);
        // console.log(`SimulateExam: Setting ${blockQuestions.length} questions for block ${blockNumber}`);
        setQuestions(blockQuestions);
        
        // Reset answer state for new block/question
        resetQuestionState();
      } else {
        console.error('SimulateExam: No questions available in window.examAllQuestions');
        setError('No questions available for this block');
      }
    } catch (err) {
      console.error('SimulateExam: Error loading questions:', err);
      // Check if this is a quota exceeded error and display detailed message
      const quotaMessage = handleQuotaError(err);
      if (quotaMessage) {
        setError(quotaMessage);
      } else {
        setError('Failed to load questions for this block');
      }
    } finally {
      setLoading(false);
    }
  };  // Reset question state when navigating
  const resetQuestionState = useCallback(() => {
    const currentQuestionData = questions[currentQuestion - 1];
    
    if (currentQuestionData) {
      // console.log('SimulateExam: resetQuestionState for question:', currentQuestionData.id);
      
      // First, check if this question was previously answered via the response object
      if (currentQuestionData.response?.answerIds && currentQuestionData.response.answerIds.length > 0) {
        // Question has been answered before - mark as submitted
        setIsAnswerSubmitted(true);
        
        // Try to get the selected letter from answers state first
        const existingAnswer = answers[currentQuestionData.id];
        if (existingAnswer?.selectedLetter) {
          setSelectedAnswer(existingAnswer.selectedLetter);
          // console.log('SimulateExam: Restored from local state with response:', {
          //   questionId: currentQuestionData.id,
          //   selectedLetter: existingAnswer.selectedLetter
          // });
          return;
        }
        
        // Try to get from session storage  
        const sessionKey = `exam_answers_${examSession?.id || 'current'}`;
        const sessionAnswers = JSON.parse(sessionStorage.getItem(sessionKey) || '{}');
        if (sessionAnswers[currentQuestionData.id]?.selectedLetter) {
          setSelectedAnswer(sessionAnswers[currentQuestionData.id].selectedLetter);
          // console.log('SimulateExam: Restored from session storage with response:', {
          //   questionId: currentQuestionData.id,
          //   selectedLetter: sessionAnswers[currentQuestionData.id].selectedLetter
          // });
          return;
        }
        
        // As last resort, try to derive from answer ID if we have the answers loaded
        // This will be supplemented by ExamAnswerOptions when answers load
        setSelectedAnswer(null); 
        // console.log('SimulateExam: Question has previous response, will be restored by ExamAnswerOptions:', {
        //   questionId: currentQuestionData.id,
        //   answerIds: currentQuestionData.response.answerIds
        // });
        return;
      }
      
      // Second, check local state
      const previousAnswer = answers[currentQuestionData.id];
      if (previousAnswer?.selectedLetter) {
        setSelectedAnswer(previousAnswer.selectedLetter);
        setIsAnswerSubmitted(true);
        // console.log('SimulateExam: Restored previous answer from local state:', {
        //   questionId: currentQuestionData.id,
        //   selectedLetter: previousAnswer.selectedLetter
        // });
        return;
      }
      
      // Third, check session storage as backup
      const sessionKey = `exam_answers_${examSession?.id || 'current'}`;
      const sessionAnswers = JSON.parse(sessionStorage.getItem(sessionKey) || '{}');
      
      if (sessionAnswers[currentQuestionData.id]?.selectedLetter) {
        const sessionAnswer = sessionAnswers[currentQuestionData.id];
        setSelectedAnswer(sessionAnswer.selectedLetter);
        setIsAnswerSubmitted(sessionAnswer.isSubmitted || false);
        // console.log('SimulateExam: Restored answer from session storage:', sessionAnswer);
        return;
      }
    }
    
    // Fresh question - no previous answers
    setSelectedAnswer(null);
    setIsAnswerSubmitted(false);
    // console.log('SimulateExam: Fresh question, no previous answers for question:', currentQuestionData?.id || 'unknown');
  // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [currentQuestion, examSession?.id]); // Intentionally excluding questions and answers to prevent infinite loops// Handle answers loaded from ExamAnswerOptions
  const handleAnswersLoaded = useCallback((answersData) => {
    // console.log('SimulateExam: Answers loaded:', answersData);
    // Store for internal use if needed
  }, []); // Empty dependency array since this callback doesn't need to change// Handle answer submission from ExamAnswerOptions
  const handleAnswerSubmitted = (responseData) => {
    // console.log('SimulateExam: Answer submitted:', responseData);
    
    if (responseData) {
      // In exam mode, don't show correctness feedback immediately
      // Store the answer but don't set isCorrect state for UI feedback
      setIsAnswerSubmitted(true);
      
      // Store answer in answers state for navigation tracking
      const currentQuestionData = questions[currentQuestion - 1];
      if (currentQuestionData && responseData.answerIds && responseData.answerIds.length > 0) {
        // Store in local state
        const answerData = {
          answer: responseData.answerIds[0],
          selectedLetter: selectedAnswer, // Store the UI letter for quick restore
          isCorrect: responseData.isCorrect || false,
          submittedAt: new Date().toISOString(),
          timeSpent: responseData.timeSpent || 0,
          isFlagged: responseData.isFlagged || false
        };
        
        // Check if this is an answer change
        const previousAnswer = answers[currentQuestionData.id];
        const isAnswerChange = previousAnswer && previousAnswer.selectedLetter !== selectedAnswer;
        
        if (isAnswerChange) {
          // console.log('SimulateExam: Answer changed from', previousAnswer.selectedLetter, 'to', selectedAnswer);
        }
        
        setAnswers(prev => ({
          ...prev,
          [currentQuestionData.id]: answerData
        }));
        
        // Also save to session storage for persistence
        const sessionKey = `exam_answers_${examSession?.id || 'current'}`;
        const sessionAnswers = JSON.parse(sessionStorage.getItem(sessionKey) || '{}');
        sessionAnswers[currentQuestionData.id] = {
          ...answerData,
          isSubmitted: true
        };
        sessionStorage.setItem(sessionKey, JSON.stringify(sessionAnswers));
        
        // console.log('SimulateExam: Saved answer to local state and session storage:', {
        //   questionId: currentQuestionData.id,
        //   answerData,
        //   sessionKey,
        //   isAnswerChange
        // });
        
        // Update the question's response object to reflect the new answer
        if (currentQuestionData.response) {
          currentQuestionData.response.answerIds = responseData.answerIds;
          currentQuestionData.response.isCorrect = responseData.isCorrect;
          currentQuestionData.response.timeSpent = responseData.timeSpent;
          currentQuestionData.response.isFlagged = responseData.isFlagged;
          currentQuestionData.response.timestamp = new Date().toISOString();
        }
      }
      
      // In exam mode, we don't update isCorrect state to hide feedback
      // This will be revealed only in review phase
    }
  };
  // Handle question flagging
  const toggleQuestionFlag = (questionId) => {
    setQuestionFlags(prev => {
      const newFlags = {
        ...prev,
        [questionId]: !prev[questionId]
      };
      
      // Also save flag state to session storage
      const sessionKey = `exam_flags_${examSession?.id || 'current'}`;
      sessionStorage.setItem(sessionKey, JSON.stringify(newFlags));
      
      // console.log('SimulateExam: Toggled question flag:', {
      //   questionId,
      //   newFlagState: newFlags[questionId],
      //   sessionKey
      // });
      
      return newFlags;
    });
  };

  // Get current question flag status
  const isQuestionFlagged = (questionId) => {
    return questionFlags[questionId] || false;
  };

  // Load question flags from session storage
  const loadQuestionFlags = useCallback(() => {
    if (!examSession?.id) return;
    
    const sessionKey = `exam_flags_${examSession.id}`;
    const sessionFlags = JSON.parse(sessionStorage.getItem(sessionKey) || '{}');
    
    if (Object.keys(sessionFlags).length > 0) {
      // console.log('SimulateExam: Loading question flags from session storage:', sessionFlags);
      setQuestionFlags(sessionFlags);
    }
  }, [examSession?.id]);

  // Load question assets (images, videos, etc.)
  const loadQuestionAssets = useCallback(async (questionId) => {
    if (!questionId) return;
    
    setIsLoadingAssets(true);
    setAssetsError(null);
    
    try {
      const assets = await examApi.followHateoasLink(`/api/question/${questionId}/assets`);
      
      // Set assets (empty array if none exist)
      setCurrentQuestionAssets(Array.isArray(assets) ? assets : []);
    } catch (error) {
      // Handle 404 as normal case (no assets exist)
      if (error.status === 404) {
        setCurrentQuestionAssets([]);
        setAssetsError(null);
      } else {
        console.error('Error loading question assets:', error);
        setAssetsError(error.message || 'Failed to load assets');
        setCurrentQuestionAssets([]);
      }
    } finally {
      setIsLoadingAssets(false);
    }
  }, []);

  const getExamFormat = () => {
    if (!selectedExamType || !examTypes.length) {
      // console.log('getExamFormat - returning default CBSE format. selectedExamType:', selectedExamType, 'examTypes length:', examTypes.length);
      return examFormats['CBSE'];
    }
    
    // Handle both string and integer ID comparison
    const examType = examTypes.find(type => 
      type.id === selectedExamType || 
      type.id === parseInt(selectedExamType) || 
      type.id.toString() === selectedExamType
    );
    
    // console.log('getExamFormat - selectedExamType:', selectedExamType, 'type:', typeof selectedExamType);
    // console.log('getExamFormat - found examType:', examType);
    // console.log('getExamFormat - available exam types:', examTypes.map(t => ({ id: t.id, name: t.name })));
    
    if (!examType) {
      // console.log('getExamFormat - examType not found, returning CBSE default');
      return examFormats['CBSE'];
    }
    
    const format = getExamFormatByType(examType);
    // console.log('getExamFormat - final format:', format);
    
    return format;
  };

  const getQuestionsPerBlock = () => {
    return getExamFormat().questionsPerBlock;
  };

  const getTotalBlocks = () => {
    return getExamFormat().blocks;
  };  // New timer control functions
  const handleTakeBreak = () => {
    const breakDuration = 15 * 60; // 15 minutes in seconds
    setBreakTimeRemaining(breakDuration);
    setCurrentView('break');
    setSessionState('break_running');
  };

  const handlePauseResume = () => {
    if (sessionState === 'block_running') {
      setSessionState('paused');
    } else if (sessionState === 'paused') {
      setSessionState('block_running');
    }
  };
  const handleBlockTimeExpired = async () => {
    // console.log(`Block ${currentBlock} time expired`);
    
    // Stop timer by setting state to paused
    setSessionState('paused');
    
    // Mark block as completed
    setCompletedBlocks(prev => new Set([...prev, currentBlock]));
    
    // Show notification
    const confirmed = window.confirm(
      `Time's up for Block ${currentBlock}!\n\nThis block will now be closed and you cannot return to it.\n\nClick OK to continue to the break period before the next block.`
    );
    
    if (currentBlock < getTotalBlocks()) {
      // Move to break before next block
      setActiveBlock(currentBlock + 1);
      handleTakeBreak();
    } else {
      // Last block completed
      completeExam();
    }
  };
  const endCurrentBlock = async () => {
    setShowEndBlockModal(true);
  };

  const confirmEndBlock = async () => {
    setShowEndBlockModal(false);
    // console.log(`User manually ended Block ${currentBlock}`);
    
    // Calculate the full block duration and deduct from total time
    const format = getExamFormat();
    const fullBlockDuration = format.blockTimeMinutes * 60;
    
    setTimeRemaining(prev => {
      const newTotal = prev - fullBlockDuration;
      // console.log(`Deducting ${fullBlockDuration} seconds from total time. New total: ${newTotal}`);
      return Math.max(0, newTotal); // Don't go below 0
    });
    
    // Stop timer
    setSessionState('paused');
    
    // Mark block as completed
    setCompletedBlocks(prev => new Set([...prev, currentBlock]));
    
    if (currentBlock < getTotalBlocks()) {
      // Move to break before next block
      setActiveBlock(currentBlock + 1);
      handleTakeBreak();
    } else {
      // Last block completed
      completeExam();
    }
  };

  const cancelEndBlock = () => {
    setShowEndBlockModal(false);
  };  const handleConfirmEndSession = () => {
    setShowEndSessionModal(false);
    completeExam();
  };

  const handleCancelEndSession = () => {
    setShowEndSessionModal(false);
  };  const resumeFromBreak = async () => {
    // First, stop the break by changing session state (this stops the timer)
    setSessionState('paused');
    
    // Stop the break timer by resetting break time remaining
    setBreakTimeRemaining(0);
    
    // Change view back to exam
    setCurrentView('exam');
    
    // Check if we should move to next block
    if (activeBlock > currentBlock) {
      // Moving to next block will automatically set session state to 'block_running'
      await navigateToBlock(activeBlock);
    } else {
      // Staying in current block, manually set session state to resume
      setSessionState('block_running');
    }
  };
  const skipBreak = async () => {
    // Directly call resumeFromBreak which will handle stopping the break properly
    await resumeFromBreak();
  };const navigateToQuestion = (questionNumber) => {
    if (questionNumber >= 1 && questionNumber <= getQuestionsPerBlock()) {
      setCurrentQuestion(questionNumber);
      resetQuestionState(); // Reset answer state for new question
    }
  };  const navigateToBlock = async (blockNumber) => {
    // Only allow navigation to the current active block or completed blocks for review
    if (blockNumber > activeBlock) {
      alert(`Block ${blockNumber} is not yet available. You must complete the current block first.`);
      return;
    }
      // Block access to completed blocks during exam
    if (completedBlocks.has(blockNumber) && currentView === 'exam') {
      alert(
        `Block ${blockNumber} has ended and you cannot go back to it.\n\n` +
        `Once a block is completed, it cannot be accessed again.\n\n` +
        `Please continue with the current block or take a break.`
      );
      return;
    }
    
    if (blockNumber >= 1 && blockNumber <= getTotalBlocks()) {
      // Update current block
      setCurrentBlock(blockNumber);
      
      // Load questions for the new block
      await loadQuestions(blockNumber);
      setCurrentQuestion(1);// If this is a new active block, reset the block timer
      if (blockNumber === activeBlock && !completedBlocks.has(blockNumber)) {
        const format = getExamFormat();
        const blockTimeSeconds = format.blockTimeMinutes * 60;
        setBlockTimeRemaining(blockTimeSeconds);
        
        // Always resume timer when navigating to active block (even if coming from break)
        setSessionState('block_running');
      }
      
      resetQuestionState();
    }
  };  const completeExam = async () => {
    try {
      setSessionState('finished');
      
      // console.log('SimulateExam: Completing exam with answers:', answers);
      
      // Stop exam timer service and get final time spent (like practice test)
      let finalTimeSpent = 0;
      if (examTimerRunning) {
        // console.log('SimulateExam: Stopping exam timer and forcing final update');
        examTimerService.forceUpdate();
        finalTimeSpent = examTimerService.stop();
        setExamTimerRunning(false);
      } else {
        // If timer wasn't running, get the current time spent
        finalTimeSpent = examTimerService.getCurrentTimeSpent();
      }
      // console.log('SimulateExam: Final time spent for exam end:', finalTimeSpent, 'seconds');

      // Use HATEOAS links to end exam (like practice test)
      if (examSession && examSession._links) {
        const endLink = getLinkHref(examSession._links, 'end');
        if (endLink) {
          // console.log('SimulateExam: Ending exam session using HATEOAS link:', endLink, 'with time:', finalTimeSpent);
          await examApi.endExam(endLink, finalTimeSpent);
        } else {
          console.warn('SimulateExam: No HATEOAS end link found, falling back to direct API call');
          // Fallback to direct API call if HATEOAS link not available
          await examService.endExamSession(examSession.id, { TimeSpent: finalTimeSpent });
        }
      } else {
        console.warn('SimulateExam: No examSession._links found, falling back to direct API call');
        // Fallback to direct API call if no HATEOAS links available
        await examService.endExamSession(examSession.id, { TimeSpent: finalTimeSpent });
      }

      setCurrentView('completed');
    } catch (err) {
      setError('Failed to complete exam');
      console.error('Error completing exam:', err);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };  const resetExam = () => {
    // Cleanup timers
    if (masterTimerRef.current) clearInterval(masterTimerRef.current);
    
    // Stop exam timer service if running
    if (examTimerRunning) {
      examTimerService.stop();
      setExamTimerRunning(false);
    }

    // Reset all state
    setCurrentView('setup');
    setSelectedExamType('');
    setExamSession(null);
    setCurrentBlock(1);
    setCurrentQuestion(1);
    setQuestions([]);
    setAnswers({});
    setTimeRemaining(0);
    setBlockTimeRemaining(0);
    setTotalExamTimeSpent(0);
    setBreakTimeRemaining(0);
    setSessionState('not_started');
    setCompletedBlocks(new Set());
    setActiveBlock(1);
    setExamStartTime(null);
    setError('');
    setIsQuotaErrorState(false);
      // Reset answer handling state
    setSelectedAnswer(null);
    setIsAnswerSubmitted(false);
    setQuestionFlags({});
    setSubmitTrigger(0);
    
    // Clear stored questions
    window.examAllQuestions = null;
  };

  // Load exam progress from session storage
  const loadExamProgress = useCallback(() => {
    if (!examSession?.id) return;
    
    const sessionKey = `exam_answers_${examSession.id}`;
    const sessionAnswers = JSON.parse(sessionStorage.getItem(sessionKey) || '{}');
    
    if (Object.keys(sessionAnswers).length > 0) {
      // console.log('SimulateExam: Loading exam progress from session storage:', {
      //   sessionKey,
      //   answersCount: Object.keys(sessionAnswers).length,
      //   answers: sessionAnswers
      // });
      
      // Convert session answers to local state format
      const localAnswers = {};
      Object.keys(sessionAnswers).forEach(questionId => {
        const sessionAnswer = sessionAnswers[questionId];
        localAnswers[questionId] = {
          answer: sessionAnswer.answer,
          selectedLetter: sessionAnswer.selectedLetter,
          isCorrect: sessionAnswer.isCorrect,
          submittedAt: sessionAnswer.submittedAt,
          timeSpent: sessionAnswer.timeSpent,
          isFlagged: sessionAnswer.isFlagged
        };
      });
      
      setAnswers(localAnswers);
      return true; // Indicates progress was loaded
    }
    
    return false; // No progress found
  }, [examSession?.id]);
  // Load exam progress when exam session is available
  useEffect(() => {
    if (examSession?.id && currentView === 'exam') {
      loadExamProgress();
      loadQuestionFlags();
    }
  }, [examSession?.id, currentView, loadExamProgress, loadQuestionFlags]);  // Clear exam progress from session storage (available for exam completion/reset)
  const clearExamProgress = useCallback(() => {
    if (!examSession?.id) return;
    
    const answersSessionKey = `exam_answers_${examSession.id}`;
    const flagsSessionKey = `exam_flags_${examSession.id}`;
    
    sessionStorage.removeItem(answersSessionKey);
    sessionStorage.removeItem(flagsSessionKey);
    
    // console.log('SimulateExam: Cleared exam progress from session storage:', {
    //   answersKey: answersSessionKey,
    //   flagsKey: flagsSessionKey
    // });
  }, [examSession?.id]);

  // Expose clearExamProgress for external use (testing, completion, etc.)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.clearExamProgress = clearExamProgress;
    }
  }, [clearExamProgress]);

  // Debug helper - expose state for testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.examDebugState = {
        currentQuestion,
        currentBlock,
        selectedAnswer,
        isAnswerSubmitted,
        answers,
        questions: questions.map(q => ({
          id: q.id,
          hasResponse: !!q.response,
          responseAnswerIds: q.response?.answerIds
        })),
        sessionStorage: {
          answers: JSON.parse(sessionStorage.getItem(`exam_answers_${examSession?.id || 'current'}`) || '{}'),
          flags: JSON.parse(sessionStorage.getItem(`exam_flags_${examSession?.id || 'current'}`) || '{}')
        }
      };
    }
  }, [currentQuestion, currentBlock, selectedAnswer, isAnswerSubmitted, answers, questions, examSession?.id]);
  
  // Render exam setup
  if (currentView === 'setup') {
    // console.log('🖼️ Rendering setup view with error:', error);
    return (
      <>
        <ExamSetupContainer title="Simulate Exam">
          <ExamSetupForm
            examTypes={examTypes}
            selectedExamType={selectedExamType}
            setSelectedExamType={setSelectedExamType}
            loading={loading}
            error={error}
            isQuotaError={isQuotaErrorState}
            onStartExam={startExam}
            getExamFormat={getExamFormat}
          />
        </ExamSetupContainer>
      </>
    );
  }

  // Render break screen
  if (currentView === 'break') {
    return (
      <div className="simulate-exam break-screen-container" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="simulate-exam-container">
          <div className="break-screen">
            <h2>Break Time</h2>
            <p className="break-subtitle">Block {completedBlocks.size} completed</p>
              <div className="break-timer">
              <div className="timer-display">
                {formatTime(breakTimeRemaining)}
              </div>
              <p>remaining break time</p>
              
              <div className="total-timer-display">
                <div className={`timer ${timeRemaining < 1800 ? 'warning' : ''}`}>
                  {formatTime(timeRemaining)}
                </div>
                <p>total exam time remaining</p>
              </div>
            </div>
              <div className="break-info">
              <p>You are currently on a break between blocks.</p>
              <p><strong>⚠️ Break time counts against your total exam time.</strong></p>
              <p>Your block timer is paused, but the exam timer continues running.</p>
              <p>You can resume the exam at any time or wait for the break to end automatically.</p>
              
              {activeBlock <= getTotalBlocks() && (
                <div className="next-block-info">
                  <h3>Next: Block {activeBlock}</h3>
                  <p>{getQuestionsPerBlock()} questions • {getExamFormat().blockTimeMinutes} minutes</p>
                </div>
              )}
            </div>

            <div className="break-actions">              <button className="resume-exam-btn primary" onClick={resumeFromBreak}>
                {activeBlock <= getTotalBlocks() ? `Start Block ${activeBlock}` : 'Continue Exam'}
              </button>
              
              {breakTimeRemaining > 0 && (
                <button className="skip-break-btn secondary" onClick={skipBreak}>
                  Skip Break
                </button>
              )}
            </div>            {activeBlock > getTotalBlocks() && (
              <div className="exam-completion-notice">
                <p>🎉 All blocks completed! You can end the exam when ready.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render completion screen
  if (currentView === 'completed') {
    return (
      <div className="simulate-exam completion-screen-container" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="simulate-exam-container">
            <div className="exam-completed">
              <h2>Exam Completed!</h2>
              <p>Congratulations! You have successfully completed the simulated exam.</p>
              <p>Your answers have been submitted and will be processed.</p>
              <p>You can return to the dashboard to view your exam results and take additional practice tests.</p>
              <div className="exam-summary">
                <h3>Exam Summary</h3>
                <p>Questions Answered: {Object.keys(answers).length}</p>
                <p>Total Questions: {getExamFormat().totalQuestions}</p>
                <p>Time Started: {examStartTime?.toLocaleTimeString()}</p>
                <p>Time Completed: {new Date().toLocaleTimeString()}</p>
              </div>
              <div className="completion-actions">
                <button className="new-exam-btn secondary" onClick={resetExam}>
                  Take Another Exam
                </button>
                <button className="dashboard-btn primary" onClick={() => navigate('/dashboard')}>
                  Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
    );
  }
  
  // Render exam interface (explicit condition for clarity)
  if (currentView === 'exam') {
    // Add debugging and explicit exam view condition
    // console.log('SimulateExam: Rendering exam interface. currentView:', currentView);
    // console.log('SimulateExam: questions.length:', questions.length);
    // console.log('SimulateExam: currentQuestion:', currentQuestion);
    // console.log('SimulateExam: currentBlock:', currentBlock);
    
    const currentQuestionData = questions[currentQuestion - 1];

    // Debug current question structure
    if (currentQuestionData) {
      // console.log('SimulateExam: Current question data:', currentQuestionData);
      // console.log('SimulateExam: Question answers:', currentQuestionData.answers || currentQuestionData.answerOptions);
    }
    
    return (
      <div className="simulate-exam exam-interface-container" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="simulate-exam-container">
            {/* Header with timer and navigation */}
        <div className="exam-header">
          <div className="exam-info">
            <span>Block {currentBlock} of {getTotalBlocks()}</span>
            <span>Question {currentQuestion} of {getQuestionsPerBlock()}</span>
          </div>
          
          <div className="exam-timers">
            <div className="timer-group">
              <label>Block Time</label>
              <div className={`timer block-timer ${blockTimeRemaining < 300 ? 'warning' : ''}`}>
                {formatTime(blockTimeRemaining)}
              </div>
            </div>
            <div className="timer-group">
              <label>Total Time</label>
              <div className={`timer total-timer ${timeRemaining < 1800 ? 'warning' : ''}`}>
                {formatTime(timeRemaining)}
              </div>
            </div>            {(sessionState === 'paused' || sessionState === 'break_running') && <span className="paused-indicator">
              {sessionState === 'break_running' ? 'ON BREAK (Time Running)' : 'PAUSED'}
            </span>}
          </div>

          <div className="exam-actions">
            <button 
              className="end-block-btn"
              onClick={endCurrentBlock}
              disabled={completedBlocks.has(currentBlock)}
              title={completedBlocks.has(currentBlock) ? 'Block already completed' : 'End this block (cannot return)'}
            >
              End This Block
            </button>
            <button 
              className="pause-resume-btn" 
              onClick={handlePauseResume}
              disabled={sessionState === 'break_running' || sessionState === 'finished'}
              title={sessionState === 'paused' ? 'Resume exam' : 'Pause exam'}
            >
              {sessionState === 'paused' ? 'Resume' : 'Pause'}
            </button>            <button 
              className="take-break-btn" 
              onClick={handleTakeBreak} 
              disabled={sessionState === 'break_running' || sessionState === 'finished'}
              title="Take a break (break time counts against total exam time)"
            >
              Take Break
            </button>
            <button className="end-exam-btn" onClick={() => setShowEndSessionModal(true)}>
              End Exam
            </button>
          </div>
        </div>

        {/* Block navigation */}
        <div className="block-navigation">
          {Array.from({ length: getTotalBlocks() }, (_, i) => i + 1).map(blockNum => {
            const isCompleted = completedBlocks.has(blockNum);
            const isActive = blockNum === currentBlock;
            const isAccessible = blockNum <= activeBlock;
            const isLocked = blockNum > activeBlock;
            const isBlockedFromAccess = isCompleted && currentView === 'exam'; // Block completed blocks during exam
            
            return (
              <button
                key={blockNum}
                className={`block-btn ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isLocked ? 'locked' : ''} ${isBlockedFromAccess ? 'blocked' : ''}`}
                onClick={() => navigateToBlock(blockNum)}
                disabled={isLocked || isBlockedFromAccess}                title={
                  isLocked 
                    ? `Block ${blockNum} is locked. Complete Block ${activeBlock - 1} first.`
                    : isBlockedFromAccess
                      ? `Block ${blockNum} has ended. You cannot return to it.`
                      : isCompleted 
                        ? `Block ${blockNum} completed`
                        : `Block ${blockNum}`
                }
              >
                Block {blockNum}
                {isCompleted && <span className="block-status">✓</span>}
                {isLocked && <span className="block-status">🔒</span>}
                {isBlockedFromAccess && <span className="block-status">🚫</span>}
              </button>
            );
          })}
        </div>{/* Question content */}
        <div className="exam-content">
          {error && (
            <div className="error-message">
              <div>{error}</div>
              {isQuotaErrorState && (
                <div style={{ marginTop: '8px' }}>
                  <Link 
                    to="/usage" 
                    style={{ 
                      color: '#007bff', 
                      textDecoration: 'underline',
                      fontWeight: '500'
                    }}
                  >
                    Click Here to Upgrade your plan
                  </Link>
                </div>
              )}
            </div>
          )}
            {currentQuestionData ? (
            <div className="question-container">
              <div className="question-header">
                <h3>Question {currentQuestion}</h3>
                {/* Flag button */}
                <button
                  className={`flag-btn ${isQuestionFlagged(currentQuestionData.id) ? 'flagged' : ''}`}
                  onClick={() => toggleQuestionFlag(currentQuestionData.id)}
                  title={isQuestionFlagged(currentQuestionData.id) ? 'Unflag question' : 'Flag question for review'}
                >
                  <FontAwesomeIcon icon={faFlag} />
                  {isQuestionFlagged(currentQuestionData.id) ? ' Flagged' : ' Flag'}
                </button>
              </div>
              
              <div className="question-text">
                <p dangerouslySetInnerHTML={{ 
                  __html: currentQuestionData.questionText || 
                         currentQuestionData.text || 
                         currentQuestionData.body || 
                         'Question text not available' 
                }}></p>
              </div>

              {/* Question Assets - images, videos, audio associated with the question */}
              <AssetDisplay 
                assets={currentQuestionAssets}
                isLoading={isLoadingAssets}
                error={assetsError}
                isDarkMode={isDarkMode}
              />

              {/* Lab data table if available */}
              <LabTable 
                currentQuestion={currentQuestionData}
                isDarkMode={isDarkMode}
              />{/* Answer options using ExamAnswerOptions component */}
              <ExamAnswerOptions
                ref={examAnswerOptionsRef}
                key={`question-${currentQuestionData.id}`}
                currentQuestion={currentQuestionData}
                selectedAnswer={selectedAnswer}
                setSelectedAnswer={setSelectedAnswer}
                isAnswerSubmitted={isAnswerSubmitted}
                isCorrect={false} // Always false in exam mode to hide feedback                isFlagged={isQuestionFlagged(currentQuestionData.id)}
                isDarkMode={isDarkMode}
                examSession={examSession} // Pass examSession for end date checking
                onAnswerSubmitted={handleAnswerSubmitted}
                onAnswersLoaded={handleAnswersLoaded}
                submitTrigger={submitTrigger}
                hideButtons={true} // We'll use our own navigation buttons
                isLocked={false} // Allow answer changes in exam mode
              />
            </div>
          ) : (
            <div className="loading-question">
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Loading question...</span>
                </>
              ) : (
                questions.length > 0 ? 'Loading question...' : 'No questions available'
              )}
            </div>
          )}
        </div>

        {/* Question navigation */}
        <div className="question-navigation">
          <div className="nav-buttons">
            <button
              className="nav-btn prev"
              onClick={() => navigateToQuestion(currentQuestion - 1)}
              disabled={currentQuestion === 1}
            >
              Previous
            </button>            {/* Submit Answer button for current question */}
            {selectedAnswer && (
              <button
                className={`nav-btn submit ${isAnswerSubmitted ? 'update' : ''}`}
                onClick={() => {
                  if (examAnswerOptionsRef.current && examAnswerOptionsRef.current.submitAnswer) {
                    examAnswerOptionsRef.current.submitAnswer();
                  } else {
                    setSubmitTrigger(prev => prev + 1);
                  }
                }}
                title={isAnswerSubmitted ? 'Update your answer selection' : 'Submit your answer'}
              >
                {isAnswerSubmitted ? 'Update Answer' : 'Submit Answer'}
              </button>
            )}
            
            <button
              className="nav-btn next"
              onClick={() => navigateToQuestion(currentQuestion + 1)}
              disabled={currentQuestion === getQuestionsPerBlock()}
            >
              Next
            </button>          </div>          {/* Question grid with status indicators */}
          <div className="question-grid">
            {Array.from({ length: getQuestionsPerBlock() }, (_, i) => i + 1).map(qNum => {
              const questionData = questions[qNum - 1];
              const isAnswered = questionData && answers[questionData.id];
              const isFlagged = questionData && isQuestionFlagged(questionData.id);
              const isCurrent = qNum === currentQuestion;
              
              // Debug the state of each question button
              // console.log(`Question ${qNum}: isCurrent=${isCurrent}, isAnswered=${isAnswered}, isFlagged=${isFlagged}`);
              
              return (
                <button
                  key={qNum}
                  className={`question-number ${isCurrent ? 'current' : ''} ${
                    isAnswered ? 'answered' : ''
                  } ${isFlagged ? 'flagged' : ''}`}
                  onClick={() => navigateToQuestion(qNum)}
                  title={`Question ${qNum}${isAnswered ? ' (Answered)' : ''}${isFlagged ? ' (Flagged)' : ''}`}
                >
                  {qNum}
                  {isFlagged && <FontAwesomeIcon icon={faFlag} className="flag-indicator" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* End Block Confirmation Modal */}
      {showEndBlockModal && (
        <div className="modal-overlay" onClick={cancelEndBlock}>
          <div className="end-block-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <h3>Confirm End Block</h3>
              <p>Are you sure you want to end Block {currentBlock}?</p>
              <div className="warning-box">
                <span className="warning-icon">⚠️</span>
                <span className="warning-text">
                  <strong>Warning:</strong> Once you end this block, you cannot return to it. Any unanswered questions will remain unanswered.
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-cancel" onClick={cancelEndBlock}>
                Cancel
              </button>
              <button className="btn btn-confirm" onClick={confirmEndBlock}>
                End Block
              </button>
            </div>
          </div>
        </div>
      )}

      {/* End Session Confirmation Modal */}
      {showEndSessionModal && (
        <div className="modal-overlay" onClick={handleCancelEndSession}>
          <div className="end-session-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>End Session</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to end this exam session?</p>
              <div className="warning-message">
                <span className="warning-icon">⚠️</span>
                <span>This action cannot be undone. Your progress will be saved.</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCancelEndSession}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleConfirmEndSession}>
                End Session
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    );
  }

  // Fallback render - should not reach here if currentView is properly set
  console.error('SimulateExam: Unexpected currentView value:', currentView);
  return (
    <div className="simulate-exam fallback-error-container" data-theme={isDarkMode ? 'dark' : 'light'}>
      <div className="simulate-exam-container">
        <div className="exam-setup">
          <h1>Error</h1>
          <p>Unexpected view state: {currentView}</p>
          <button onClick={() => setCurrentView('setup')}>Return to Setup</button>
        </div>
      </div>
    </div>
  );
};

export default SimulateExam;